import type {
  TenantAboutPageContent,
  TenantHomePageContent,
  TenantServicesPageContent,
} from '../types/site-pages';

const fallbackPhotoUrl = (path: string) =>
  `https://firebasestorage.googleapis.com/v0/b/revalidatie-website.firebasestorage.app/o/${encodeURIComponent(path)}?alt=media`;

const defaultHomeHeroImageUrl =
  'https://images.pexels.com/photos/7579831/pexels-photo-7579831.jpeg?auto=compress&cs=tinysrgb&w=1600';

export const defaultHomePageContent: TenantHomePageContent = {
  hero: {
    title: 'Cuidado especializado para sua reabilitação',
    subtitle:
      'Nossa equipe oferece serviços especializados em fisioterapia e reabilitação, com foco na qualidade de vida e recuperação dos pacientes.',
    desktopImage: {
      provider: 'external-url',
      downloadUrl: defaultHomeHeroImageUrl,
      alt: 'Profissional realizando atendimento de fisioterapia',
    },
    mobileImage: {
      provider: 'external-url',
      downloadUrl: defaultHomeHeroImageUrl,
      alt: 'Profissional realizando atendimento de fisioterapia',
    },
    primaryCta: {
      label: 'Agendar Consulta',
      path: '/agendar',
      variant: 'primary',
    },
    secondaryCta: {
      label: 'Nossos Serviços',
      path: '/servicos',
      variant: 'secondary',
    },
  },
  features: {
    title: 'Por que escolher nossa clínica?',
    subtitle:
      'Serviços de fisioterapia de alta qualidade com foco na recuperação e bem-estar dos pacientes.',
    items: [
      {
        id: 'home-feature-care',
        iconKey: 'heart',
        title: 'Cuidado Personalizado',
        description: 'Tratamentos adaptados às necessidades individuais de cada paciente.',
      },
      {
        id: 'home-feature-cardio',
        iconKey: 'activity',
        title: 'Especialistas em Cardiorrespiratória',
        description: 'Equipe especializada em reabilitação cardíaca e respiratória.',
      },
      {
        id: 'home-feature-oncology',
        iconKey: 'shield',
        title: 'Reabilitação Oncológica',
        description: 'Suporte especializado para pacientes em tratamento oncológico.',
      },
      {
        id: 'home-feature-team',
        iconKey: 'users',
        title: 'Equipe Multidisciplinar',
        description: 'Profissionais de diversas especialidades trabalhando em conjunto.',
      },
      {
        id: 'home-feature-excellence',
        iconKey: 'award',
        title: 'Excelência Reconhecida',
        description: 'Comprometimento com os mais altos padrões de qualidade e segurança.',
      },
      {
        id: 'home-feature-education',
        iconKey: 'book-open',
        title: 'Educação Continuada',
        description: 'Recursos educacionais para pacientes e familiares sobre saúde e reabilitação.',
      },
    ],
  },
  testimonials: {
    title: 'O que nossos pacientes dizem',
    subtitle: 'Histórias reais de pacientes que transformaram suas vidas com nossos tratamentos.',
  },
  newsPreview: {
    title: 'Notícias e Artigos',
    subtitle:
      'Fique por dentro das novidades, técnicas e avanços na área de fisioterapia e reabilitação.',
    viewAllLabel: 'Ver todas as notícias',
  },
  cta: {
    title: 'Comece sua jornada de recuperação hoje mesmo',
    body:
      'Nossa equipe especializada está pronta para criar um plano de tratamento personalizado para suas necessidades.',
    primaryCta: {
      label: 'Agendar Consulta',
      path: '/agendar',
      variant: 'primary',
    },
    secondaryCta: {
      label: 'Fale Conosco',
      path: '/contato',
      variant: 'secondary',
    },
  },
};

export const mergeHomePageContent = (
  input?: TenantHomePageContent | null,
): TenantHomePageContent => ({
  hero: {
    ...defaultHomePageContent.hero,
    ...input?.hero,
    desktopImage: input?.hero?.desktopImage ?? defaultHomePageContent.hero?.desktopImage,
    mobileImage: input?.hero?.mobileImage ?? defaultHomePageContent.hero?.mobileImage,
    primaryCta: input?.hero?.primaryCta ?? defaultHomePageContent.hero?.primaryCta,
    secondaryCta: input?.hero?.secondaryCta ?? defaultHomePageContent.hero?.secondaryCta,
  },
  features: {
    ...defaultHomePageContent.features,
    ...input?.features,
    items: input?.features?.items?.length
      ? input.features.items
      : defaultHomePageContent.features?.items,
  },
  testimonials: {
    ...defaultHomePageContent.testimonials,
    ...input?.testimonials,
  },
  newsPreview: {
    ...defaultHomePageContent.newsPreview,
    ...input?.newsPreview,
  },
  cta: {
    ...defaultHomePageContent.cta,
    ...input?.cta,
    primaryCta: input?.cta?.primaryCta ?? defaultHomePageContent.cta?.primaryCta,
    secondaryCta: input?.cta?.secondaryCta ?? defaultHomePageContent.cta?.secondaryCta,
  },
});

export const defaultAboutPageContent: TenantAboutPageContent = {
  hero: {
    title: 'Sobre Nós',
  },
  intro: {
    body:
      'Bem-vindo à Revalidiate, onde a fisioterapia é praticada com um atendimento humanizado. Nossa missão é oferecer cuidados de saúde excepcionais, com foco no bem-estar completo de nossos pacientes.',
  },
  mission: {
    title: 'Nossa Missão',
    body:
      'Oferecer atendimento de excelência, combinando a assistência baseada em evidências científicas com o cuidado personalizado, garantindo o melhor resultado possível para cada paciente.',
  },
  values: {
    title: 'Valores',
    items: [
      'Excelência no atendimento',
      'Ética profissional',
      'Compromisso com resultados',
      'Inovação constante',
    ],
  },
  differentials: {
    title: 'Diferenciais',
    items: [
      'Equipe altamente qualificada (todos os profissionais possuem Doutorado ou PhD)',
      'Tecnologia (todas avaliações e atendimentos seguem as diretrizes nacionais e internacionais mais atuais na área)',
      'Atendimento humanizado',
      'Instalações modernas',
    ],
  },
  history: {
    title: 'Nossa História',
    paragraphs: [
      'Com 20 anos de experiência no setor de saúde, nossos profissionais tem se destacado pela busca constante pela excelência e inovação. Nosso compromisso com a qualidade e o bem-estar dos pacientes nos tornou referência em atendimento na Fisioterapia Cardiorrespiratória e Oncológica.',
      'Investimos continuamente em tecnologia e capacitação profissional para oferecer o que há de mais atual em tratamentos na área de Fisioterapia Cardiorrespiratória e Oncológica.',
    ],
  },
  team: {
    title: 'Nossa Equipe',
    members: [
      {
        id: 'carlos-augusto-camillo',
        name: 'Dr. Carlos Augusto Camillo',
        role: 'Fisioterapeuta',
        credentials: [
          'Pós-Doutorado em Ciências da Reabilitação pela Universidade Estadual de Londrina (UEL).',
          'Doutorado em Rehabilitation Sciences and Physiotherapy pela Katholieke Universiteit Leuven (KU Leuven), Bélgica.',
          'Mestrado em Fisioterapia pela Universidade Estadual Paulista (UNESP).',
          'Residência em Fisioterapia Pulmonar pela Universidade Estadual de Londrina (UEL).',
          'Graduação em Fisioterapia pela Universidade Estadual de Londrina (UEL).',
        ],
        sortOrder: 1,
        active: true,
        photo: {
          provider: 'external-url',
          storagePath: 'team/dr-carlos-new.JPG',
          downloadUrl: fallbackPhotoUrl('team/dr-carlos-new.JPG'),
          alt: 'Dr. Carlos Augusto Camillo',
        },
      },
      {
        id: 'fernanda-kazmierski-morakami',
        name: 'Dra. Fernanda Kazmierski Morakami',
        role: 'Fisioterapeuta',
        credentials: [
          'Doutorado em Ciências da Saúde pela Universidade Estadual de Londrina (UEL).',
          'Mestrado em Ciências da Reabilitação pela Universidade Estadual de Londrina (UEL).',
          'Especialização em Fisioterapia em Terapia Intensiva (ASSOBRAFIR)',
          'Especialização em Fisioterapia na Emergência pela Universidade Federal de São Paulo (UNIFESP).',
          'Graduação em Fisioterapia pela Universidade Estadual de Londrina (UEL)',
        ],
        sortOrder: 2,
        active: true,
        photo: {
          provider: 'external-url',
          storagePath: 'team/dr-fernanda-new.jpeg',
          downloadUrl: fallbackPhotoUrl('team/dr-fernanda-new.jpeg'),
          alt: 'Dra. Fernanda Kazmierski Morakami',
        },
      },
      {
        id: 'humberto-silva',
        name: 'Dr. Humberto Silva',
        role: 'Fisioterapeuta',
        credentials: [
          'Doutorado em andamento em Ciências da Saúde pela Universidade Estadual de Londrina (UEL).',
          'Mestrado em Ciências da Reabilitação pela Universidade Estadual de Londrina (UEL).',
          'Residência em Fisioterapia Pulmonar pela Universidade Estadual de Londrina (UEL).',
          'Graduação em Fisioterapia pelo Centro Universitário Filadelfia (UNIFIL)',
        ],
        sortOrder: 3,
        active: true,
        photo: {
          provider: 'external-url',
          storagePath: 'team/dr-humberto-new.jpeg',
          downloadUrl: fallbackPhotoUrl('team/dr-humberto-new.jpeg'),
          alt: 'Dr. Humberto Silva',
        },
      },
    ],
  },
  cta: {
    title: 'Agende sua Consulta',
    body: 'Entre em contato conosco e conheça nossos serviços especializados.',
    primaryCta: {
      label: 'Agendar Agora',
      path: '/agendar',
      variant: 'primary',
    },
  },
};

export const mergeAboutPageContent = (
  input?: TenantAboutPageContent | null,
): TenantAboutPageContent => ({
  hero: {
    ...defaultAboutPageContent.hero,
    ...input?.hero,
  },
  intro: {
    ...defaultAboutPageContent.intro,
    ...input?.intro,
  },
  mission: {
    ...defaultAboutPageContent.mission,
    ...input?.mission,
  },
  values: {
    ...defaultAboutPageContent.values,
    ...input?.values,
    items: input?.values?.items?.length
      ? input.values.items
      : defaultAboutPageContent.values?.items,
  },
  differentials: {
    ...defaultAboutPageContent.differentials,
    ...input?.differentials,
    items: input?.differentials?.items?.length
      ? input.differentials.items
      : defaultAboutPageContent.differentials?.items,
  },
  history: {
    ...defaultAboutPageContent.history,
    ...input?.history,
    paragraphs: input?.history?.paragraphs?.length
      ? input.history.paragraphs
      : defaultAboutPageContent.history?.paragraphs,
  },
  team: {
    ...defaultAboutPageContent.team,
    ...input?.team,
    members: input?.team?.members?.length
      ? input.team.members
      : defaultAboutPageContent.team?.members,
  },
  cta: {
    ...defaultAboutPageContent.cta,
    ...input?.cta,
    primaryCta: input?.cta?.primaryCta ?? defaultAboutPageContent.cta?.primaryCta,
  },
});

export const defaultServicesPageContent: TenantServicesPageContent = {
  hero: {
    title: 'Nossos Serviços',
    subtitle:
      'Oferecemos uma ampla gama de serviços especializados com profissionais altamente qualificados.',
  },
  intro: {
    title: 'Atendimento especializado para cada necessidade',
    body:
      'Nossa clínica reúne avaliação, monitorização e planos terapêuticos personalizados para apoiar cada etapa da reabilitação com segurança e precisão clínica.',
  },
  services: {
    title: 'Especialidades e atendimentos',
    subtitle:
      'Conheça os principais serviços que podem ser personalizados por tenant conforme a atuação da clínica.',
    items: [
      {
        id: 'services-respiratory',
        iconKey: 'stethoscope',
        title: 'Fisioterapia Respiratória',
        description: 'Atendimento especializado para todas as doenças respiratórias.',
      },
      {
        id: 'services-pulmonary-rehab',
        iconKey: 'dumbbell',
        title: 'Reabilitação Pulmonar',
        description:
          'Programa de reabilitação física com foco na melhora da condição física, redução de sintomas e melhora da qualidade de vida de portadores de doenças respiratórias.',
      },
      {
        id: 'services-cardiac-rehab',
        iconKey: 'heart',
        title: 'Reabilitação Cardíaca',
        description:
          'Programa de reabilitação física individual ou em grupo para portadores de doenças cardíacas, com monitorização cardiovascular avançada.',
      },
      {
        id: 'services-diagnostic-tests',
        iconKey: 'activity',
        title: 'Exames Diagnósticos',
        description:
          'Ampla gama de testes e avaliações incluindo testes de função pulmonar, testes de campo, morfologia musculoesquelética e aptidão cardiorrespiratória.',
      },
      {
        id: 'services-home-care',
        iconKey: 'home',
        title: 'Atendimento Domiciliar',
        description:
          'Atendimento em domicílio para pacientes com dificuldade de locomoção ou que preferem o conforto do lar para sua reabilitação.',
      },
      {
        id: 'services-functional-assessment',
        iconKey: 'microscope',
        title: 'Avaliações Físico-Funcionais',
        description:
          'Avaliações clínicas com testes de função pulmonar, testes de campo, estrutura musculoesquelética e aptidão cardiorrespiratória.',
      },
    ],
  },
  cta: {
    title: 'Precisa de atendimento especializado?',
    body: 'Entre em contato conosco para agendar sua consulta e entender qual serviço faz mais sentido para o seu caso.',
    primaryCta: {
      label: 'Agendar Consulta',
      path: '/agendar',
      variant: 'primary',
    },
  },
};

export const mergeServicesPageContent = (
  input?: TenantServicesPageContent | null,
): TenantServicesPageContent => ({
  hero: {
    ...defaultServicesPageContent.hero,
    ...input?.hero,
    backgroundImage:
      input?.hero?.backgroundImage ?? defaultServicesPageContent.hero?.backgroundImage,
  },
  intro: {
    ...defaultServicesPageContent.intro,
    ...input?.intro,
  },
  services: {
    ...defaultServicesPageContent.services,
    ...input?.services,
    items: input?.services?.items?.length
      ? input.services.items
      : defaultServicesPageContent.services?.items,
  },
  cta: {
    ...defaultServicesPageContent.cta,
    ...input?.cta,
    primaryCta: input?.cta?.primaryCta ?? defaultServicesPageContent.cta?.primaryCta,
  },
});
